import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  getColor,
  Icons,
  InputField,
  Tabs,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { ActionResponse, LoginDto } from "../../types/auth";
import { useNavigate, useParams } from "react-router-dom";
import utilsCommon from "../../shared/utils/common";
import { nanoid } from "@reduxjs/toolkit";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import { useTranslation } from "react-i18next";
import PolicyModal from "../PolicyModal";
import useWorkspaceStore from "../../redux/dispatcher/useWorkspaceStore";
import i18n from "../../locales/i18n";
import classNames from "classnames";
import {
  getUserInfoFromLocalStorage,
  removeRedirectUrl,
} from "../../shared/utils/auth";
interface SignupProps {
  actionInfo?: ActionResponse;
}
const Signup: React.FC<SignupProps> = ({ actionInfo }) => {
  const navigate = useNavigate();
  const toasts = useToast();
  const { t } = useTranslation();
  const { setJoinMember, checkUserValidSignupUser, getPasswdPolicy } =
    useLoginStore();
  const { doSignupInWorkspace, verifyMemberMailAgain } = useWorkspaceStore();
  const urlParmas = new URLSearchParams(window.location.search);

  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const [passwordPolicy, changePasswordPolicy] = useState({
    minLength: 6,
    maxLength: 30,
  });
  const [userInfo, changeUserInfo] = useState({
    email: "",
    userId: "",
    password: "",
    showPassword: false,
    checkPassword: "",
    showCheckPassword: false,
    userName: "",
    deptName: "",
    phoneNumber: "",
    isConfirmCollectInfo: false, //개인정보 수집, 이용, 제공에 동의
  });
  const [isOpenPrivayModal, toggleOpenPrivayModal] = useState(false);
  const [err, changeErr] = useState({ target: "NONE", message: "NONE" });
  const [step, changeStep] = useState(0); // 0: 입력, 1: 이메일 인증 중
  const [isCheckSignup, setIsCheckSignup] = useState(false);
  useEffect(() => {
    getPasswordPolicy();

    if (
      !actionInfo &&
      window.eco.serviceType !== "OCI" &&
      (urlParmas.get("uniqueId")?.trim() === "" ||
        !loginDto?.userInfo?.userId ||
        (loginDto && loginDto?.ecoUserInfo?.userType !== 3))
    ) {
      window.location.href = `${process.env.PUBLIC_URL}/checkSignup`;
      return;
    }
    if (actionInfo) {
      changeUserInfo({
        ...userInfo,
        email: actionInfo.targetValue,
        userId: actionInfo?.targetValue,
      });
      setIsCheckSignup(true);
    } else {
      changeUserInfo({
        ...userInfo,
        email: loginDto?.userInfo?.userId ?? "",
        userId: loginDto?.userInfo?.userId ?? "",
      });
    }
  }, [actionInfo]);
  const getPasswordPolicy = async () => {
    let resp: any = await getPasswdPolicy();
    if (resp.payload) {
      changePasswordPolicy({
        minLength: resp.payload.minLength,
        maxLength: resp.payload.maxLength,
      });
    }
  };
  /** 회원가입 버튼 submit */
  const handleSignup = async () => {
    if (
      !actionInfo &&
      window.eco.serviceType !== "OCI" &&
      (urlParmas.get("uniqueId")?.trim() === "" || !loginDto?.userInfo?.userId)
    )
      return;
    if (!utilsCommon.emailValidation(userInfo.userId)) {
      changeErr({
        target: "userId",
        message: t("signup.noValidEmailType"),
      });
      return;
    }
    if (
      !utilsCommon.passwordValidation(
        userInfo.password,
        passwordPolicy.minLength,
        passwordPolicy.maxLength
      )
    ) {
      changeErr({
        target: "password",
        message: t("signup.alert.invalidPwMsg", {
          min: passwordPolicy.minLength,
          max: passwordPolicy.maxLength,
        }),
      });
      return;
    }
    if (userInfo.password !== userInfo.checkPassword) {
      changeErr({
        target: "checkPassword",
        message: t("login.changePW.alert.notMatchPwMsg"),
      });
      return;
    }
    changeErr({ target: "NONE", message: "NONE" });
    const resp: any = await (window.eco.serviceType !== "OCI" && actionInfo
      ? setJoinMember(
          urlParmas?.get("uniqueId")?.trim() ?? actionInfo.uniqueId,
          [
            {
              userId: userInfo.userId,
              userName: userInfo.userName,
              password: userInfo.password,
              deptName: userInfo.deptName,
              userType: 2,
              status: 3,
              etc: "",
              phoneNumber: userInfo.phoneNumber,
            },
          ]
        )
      : urlParmas.get("uniqueId") &&
        urlParmas.get("uniqueId")?.trim() !== "" &&
        loginDto?.userInfo?.userId &&
        window.eco.serviceType !== "OCI"
      ? setJoinMember(urlParmas?.get("uniqueId")?.trim() ?? "", [
          {
            userId: userInfo.userId,
            userName: userInfo.userName,
            password: userInfo.password,
            deptName: userInfo.deptName,
            userType: 2,
            status: 3,
            etc: "",
            phoneNumber: userInfo.phoneNumber,
          },
        ])
      : doSignupInWorkspace({
          user: {
            companyName: userInfo.deptName,
            password: userInfo.password,
            phoneNumber: userInfo.phoneNumber,
            userId: userInfo.userId,
            userName: userInfo.userName,
          },
          withInvitation: !!actionInfo,
        }));
    if (resp.payload) {
      if (window.eco.serviceType === "OCI" && !isCheckSignup) {
        changeStep(1);
      } else {
        toasts.toastMsg(nanoid(), t("signup.alert.signUpMsg"), "success");
        navigate(`${process.env.PUBLIC_URL}/login`);
      }
    } else if (resp.error) {
      if (
        resp.error.message === "DUPLICATE_MEMBER" ||
        resp.error.message === "DUPLICATE_FED5_USER"
      ) {
        toasts.toastMsg(nanoid(), t("signup.alert.duplicateMsg"), "error");
      } else {
        toasts.toastMsg(
          nanoid(),
          resp.error.message ?? t("signup.alert.errorSignUpMsg"),
          "error"
        );
      }
    }
  };
  const handleReInviteUser = async (userId: string): Promise<void> => {
    const resp: any =
      window.eco.serviceType === "OCI"
        ? await verifyMemberMailAgain(userId)
        : await checkUserValidSignupUser(userId);
    if (resp.payload) {
      toasts.toastMsg(nanoid(), t("wsLogin.sendCertifyMsg"), "success");
    } else {
      toasts.toastMsg(nanoid(), t("wsLogin.errorSendCertifyMsg"), "error");
    }
  };
  return step === 0 ? (
    <Box width={"100%"} height={"100%"} classes={"signup"}>
      {isOpenPrivayModal && (
        <PolicyModal
          open={isOpenPrivayModal}
          onClose={() => toggleOpenPrivayModal(false)}
        />
      )}
      <Box height={60} alignItems={"center"} justifyContent={"center"}>
        <img
          src={utilsCommon.renderWrapsodyECOLogo()}
          alt={"logo"}
          height={32}
          width={129}
          className="cursor pointer"
          onClick={() =>
            navigate(
              loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                ? `${process.env.PUBLIC_URL}/myInbox/received`
                : `${process.env.PUBLIC_URL}/home`
            )
          }
        />
      </Box>
      <Box
        justifyContent={"center"}
        alignItems={"center"}
        style={{ marginTop: 30 }}
        maxWidth={420}
        width={"100%"}
        direction={"column"}
      >
        {window.eco.serviceType !== "OCI" ? (
          <Tabs
            type={"full"}
            selected={1}
            textAlign={"left"}
            tabs={[
              {
                color: "theme",
                text: `1. ${t("signup.checkValidUser")}`,
                isNew: false,
              },
              {
                color: "theme",
                text: `2. ${t("signup.enterInfo")}`,
                isNew: false,
              },
            ]}
            size={"md"}
          />
        ) : (
          <Box direction="column" alignItems="center">
            <Typography type="h1">{t("login.signup")}</Typography>
            <Box justifyContent={"center"} classes="mg mt-20">
              <Typography
                type={"body3_rg"}
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("signup.alreadyAccount")}
              </Typography>
              <Typography
                type={"body3_rg"}
                classes={"mg ml-4 login-button"}
                exactColor={getColor("COLOR_GREEN_60")}
                onClick={() => navigate(`${process.env.PUBLIC_URL}/login`)}
              >
                {t("signup.goLogin")}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        classes={"mg mt-40"}
        direction={"column"}
        alignItems={"left"}
        width={"360px"}
      >
        <Typography type="subtitle2">{t("signup.email")}</Typography>
        <InputField
          placeholder={t("signup.emailPlaceholder")}
          classes={"mg mt-8"}
          size={"md"}
          disabled={window.eco.serviceType !== "OCI" || isCheckSignup}
          maxLength={60}
          value={userInfo.userId}
          isError={err.target === "userId"}
          helpMessage={err.target === "userId" ? err.message : undefined}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              userId: e.target.value,
            })
          }
        />
        <InputField
          label={t("login.login.password")}
          placeholder={t("signup.passwordPlaceholder")}
          maxLength={60}
          size={"md"}
          type={userInfo.showPassword ? "text" : "password"}
          value={userInfo.password}
          isError={err.target === "password"}
          helpMessage={err.target === "password" ? err.message : undefined}
          classes={classNames("mg mt-24 login-password", {
            ar: i18n.language.includes("ar"),
          })}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              password: e.target.value,
            })
          }
          trailingIcon={
            <Box alignItems={"center"}>
              {userInfo.password?.length > 0 && (
                <span
                  className={"pwViewBtn mg mr-8"}
                  onClick={() =>
                    changeUserInfo({
                      ...userInfo,
                      password: "",
                    })
                  }
                >
                  <Icons variant="line" label="closeLine" stroke={"#717985"} />
                </span>
              )}
              <Tooltip
                message={
                  userInfo.showPassword
                    ? t("login.login.hidePW")
                    : t("login.login.showPW")
                }
              >
                <span
                  className={"pwViewBtn"}
                  onClick={() =>
                    changeUserInfo({
                      ...userInfo,
                      showPassword: !userInfo.showPassword,
                    })
                  }
                >
                  <Icons
                    variant="line"
                    label="preview"
                    isOn={!userInfo.showPassword}
                    stroke={"#717985"}
                  />
                </span>
              </Tooltip>
            </Box>
          }
        />
        <InputField
          label={t("signup.checkPassword")}
          placeholder={t("signup.passwordRequire")}
          maxLength={60}
          size={"md"}
          classes={classNames("mg mt-24 login-password", {
            ar: i18n.language.includes("ar"),
          })}
          type={userInfo.showCheckPassword ? "text" : "password"}
          value={userInfo.checkPassword}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              checkPassword: e.target.value,
            })
          }
          isError={err.target === "checkPassword"}
          helpMessage={err.target === "checkPassword" ? err.message : undefined}
          trailingIcon={
            <Box alignItems={"center"}>
              {userInfo.checkPassword?.length > 0 && (
                <span
                  className={"pwViewBtn mg mr-8"}
                  onClick={() =>
                    changeUserInfo({
                      ...userInfo,
                      checkPassword: "",
                    })
                  }
                >
                  <Icons variant="line" label="closeLine" stroke={"#717985"} />
                </span>
              )}
              <Tooltip
                message={
                  userInfo.showPassword
                    ? t("login.login.hidePW")
                    : t("login.login.showPW")
                }
              >
                <span
                  className={"pwViewBtn"}
                  onClick={() =>
                    changeUserInfo({
                      ...userInfo,
                      showCheckPassword: !userInfo.showCheckPassword,
                    })
                  }
                >
                  <Icons
                    variant="line"
                    label="preview"
                    isOn={!userInfo.showCheckPassword}
                    stroke={"#717985"}
                  />
                </span>
              </Tooltip>
            </Box>
          }
        />
        <Typography type="subtitle2" classes="mg mt-24">
          {t("signup.name")}
        </Typography>
        <InputField
          placeholder={t("signup.enterName")}
          classes={"mg mt-6"}
          size={"md"}
          maxLength={60}
          value={userInfo.userName}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              userName: e.target.value,
            })
          }
        />
        <Typography type="subtitle2" classes="mg mt-24">
          {t("signup.phone")}
        </Typography>
        <InputField
          placeholder={t("signup.phonePlaceholder")}
          classes={"mg mt-6"}
          size={"md"}
          maxLength={16}
          value={userInfo.phoneNumber}
          onChange={(e) => {
            if (!/^[0-9-]+$/.test(e.target.value) && e.target.value !== "")
              return;
            changeUserInfo({
              ...userInfo,
              phoneNumber: e.target.value,
            });
          }}
          helpMessage={t("signup.enterHyphenContact")}
        />
        <Typography type="subtitle2" classes="mg mt-24">
          {t("signup.company")}
        </Typography>
        <InputField
          placeholder={t("signup.companyPlaceholder")}
          classes={"mg mt-6"}
          size={"md"}
          maxLength={60}
          value={userInfo.deptName}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              deptName: e.target.value,
            })
          }
        />
        <Checkbox
          label={t("signup.termsAgree")}
          justifyContent="flex-start"
          classDiv={classNames("mg mt-24 signup-checkbox", {
            oci: window.eco.serviceType === "OCI",
          })}
          check={userInfo.isConfirmCollectInfo}
          labelOnClick={() =>
            window.eco.serviceType === "OCI" && toggleOpenPrivayModal(true)
          }
          onClick={() =>
            changeUserInfo({
              ...userInfo,
              isConfirmCollectInfo: !userInfo.isConfirmCollectInfo,
            })
          }
        />
        <Button
          classes={"mg mt-40"}
          btnStyle="fill"
          label={t("login.signup")}
          size={"xlg"}
          state={
            !userInfo.userName.trim() ||
            !userInfo.password ||
            !userInfo.deptName.trim() ||
            !userInfo.isConfirmCollectInfo
              ? "disable"
              : "default"
          }
          onClick={handleSignup}
        />
        {window.eco.serviceType !== "OCI" && (
          <Box justifyContent={"center"} style={{ marginTop: 42 }}>
            <Typography
              type={"body3_rg"}
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("signup.alreadyAccount")}
            </Typography>
            <Typography
              type={"body3_rg"}
              classes={"mg ml-4 login-button"}
              exactColor={getColor("COLOR_GREEN_60")}
              onClick={() => navigate(`${process.env.PUBLIC_URL}/login`)}
            >
              {t("signup.goLogin")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  ) : (
    <Box width={"100%"} height={"100%"} justifyContent="center">
      <Box
        direction="column"
        alignItems="center"
        maxWidth={"60%"}
        placeSelf="center"
      >
        <Box height={60} alignItems={"center"} justifyContent={"center"}>
          <img
            src={utilsCommon.renderWrapsodyECOLogo()}
            alt={"logo"}
            height={32}
            width={129}
            className="cursor pointer"
            onClick={() =>
              navigate(
                loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                  ? `${process.env.PUBLIC_URL}/myInbox/received`
                  : `${process.env.PUBLIC_URL}/home`
              )
            }
          />
        </Box>
        <Typography type="h1" style={{ marginTop: 72 }}>
          {t("wsLogin.emailAuth")}
        </Typography>
        <Box
          direction="column"
          classes="signup-mailBody"
          alignItems="center"
          width={"100%"}
        >
          <Typography type="h1">{userInfo?.userId}</Typography>
          <Box classes="divider" />
          <Typography wordBreak="break-all" whiteSpace="pre" textAlign="center">
            {t("wsLogin.sendAuthEmail")}
          </Typography>
        </Box>
        <Box classes="mg mt-40 mb-40" width={"100%"}>
          <Button
            label={t("wsLogin.resendAuth")}
            btnStyle="fill"
            type="secondary"
            width={"50%"}
            onClick={() => handleReInviteUser(userInfo.userId)}
          />
          <Button
            label={t("common.confirm")}
            btnStyle="fill"
            classes="mg ml-8"
            width={"50%"}
            onClick={() => {
              removeRedirectUrl();
              navigate(`${process.env.PUBLIC_URL}/login`);
            }}
          />
        </Box>
        <Box classes="mg mt-40 mb-40">
          <img
            src={utilsCommon.renderWrapsodyECOLogo()}
            alt="Wrapsody eCo"
            width={96.8}
            height={23.1}
            className="mg mr-12"
          />
          <Typography exactColor={getColor("COLOR_TEXT_SECONDARY")}>
            {"© Fasoo"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Signup;
